import { Component, Inject, Injector, QueryList, ViewChild, ViewChildren, ViewContainerRef, ViewEncapsulation } from "@angular/core";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { SpeedCatchup, SpeedCatchupBreezeModel } from "@common/ADAPT.Common.Model/organisation/speed-catchup";
import { CatchupRatingType } from "@common/ADAPT.Common.Model/organisation/speed-catchup-rating";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { PersistableDialog } from "@common/lib/data/persistable-dialog.decorator";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseDialogComponent } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { DateFormats } from "@common/ux/date-formats";
import { AdaptHtmlEditorComponent } from "@common/ux/html-editor/html-editor.component";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { KanbanService } from "@org-common/lib/kanban/kanban.service";
import { KanbanUiService } from "@org-common/lib/kanban/kanban-ui.service";
import { LinkService } from "@org-common/lib/link/link.service";
import { LinkAuthService } from "@org-common/lib/link/link-auth.service";
import { PeerCatchupAuthService } from "@org-common/lib/peer-catchup/peer-catchup-auth.service";
import { EMPTY, of, Subject } from "rxjs";
import { debounceTime, filter, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";
import { ItemUtilities } from "../../kanban/items/item-utilities";
import { EditCatchupRatingTypeComponent } from "../edit-catchup-rating-type/edit-catchup-rating-type.component";
import { PeerCatchupService } from "../peer-catchup.service";
import { PeerCatchupUiService } from "../peer-catchup-ui.service";

export interface IShowCatchupDialogData {
    catchup: SpeedCatchup;
    hideHistoryButton?: boolean;
    enterEditMode?: boolean;
    showFacilitatorSection?: boolean;
    focusRatingType?: CatchupRatingType;
}

interface IAccordionEntry {
    ratingType?: CatchupRatingType;
    template: string;
    titleTemplate: string;
}

@Component({
    selector: "adapt-show-catchup-dialog",
    templateUrl: "./show-catchup-dialog.component.html",
    styleUrls: ["./show-catchup-dialog.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
@PersistableDialog("ShowCatchupDialog")
export class ShowCatchupDialogComponent extends BaseDialogComponent<IShowCatchupDialogData, SpeedCatchup> {
    public readonly dialogName = "ShowCatchupDialog";
    public readonly DateFormats = DateFormats;

    public title = SpeedCatchupBreezeModel.singularName;
    public accordionEntries: IAccordionEntry[] = [];
    // cannot use accordionEntries what that's set as it will cause error from dxAccordion as dataSource is only processed later
    public selectedEntries: IAccordionEntry[] = [];

    public catchupRatingTypes = [...CatchupRatingType.All];
    public canEditCatchup = false;
    public showGuidance = false;
    public isEditing = false;
    public isEditingUpdater = this.createThrottledUpdater((isEditing: boolean) => {
        this.isEditing = isEditing;
        this.editingUpdated.next();
    });

    public catchup: SpeedCatchup;

    @ViewChild("catchupFacilitatorSection", { read: ViewContainerRef }) private facilitatorSection?: ViewContainerRef;
    private scrollFacilitatorIntoView = new Subject<void>();
    @ViewChild("catchupFacilitatorEditor") private facilitatorEditor?: AdaptHtmlEditorComponent;

    @ViewChildren(EditCatchupRatingTypeComponent) private ratingTypeComponents!: QueryList<EditCatchupRatingTypeComponent>;
    private scrollToRatingTypeSection = new Subject<void>();
    private peerCatchupUiService: PeerCatchupUiService;

    private editingUpdated = new Subject<void>();
    public hasEditableBoard = false;

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public data: IShowCatchupDialogData,
        private peerCatchupService: PeerCatchupService,
        private dialogService: AdaptCommonDialogService,
        private linkService: LinkService,
        private kanbanUiService: KanbanUiService,
        authorisationService: AuthorisationService,
        kanbanService: KanbanService,
        catchupAuthService: PeerCatchupAuthService,
        injector: Injector,
    ) {
        super();

        linkService.linkedItemChanged$(() => this.catchup.links).pipe(
            switchMap(() => authorisationService.promiseToGetHasAccess(LinkAuthService.ViewLink)),
            switchMap((hasLinkAccess) => hasLinkAccess
                ? this.peerCatchupService.getLinksForCatchup(this.catchup.speedCatchupId)
                : of(undefined)),
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            this.initAccordionEntries();
        });

        // cannot inject this in the constructor declaration once using PersistableDialog as that's adding this dialog Type which will attempt injection
        // which will also has dependency on this type
        this.peerCatchupUiService = injector.get(PeerCatchupUiService);
        this.catchup = data.catchup;
        catchupAuthService.canEditCatchup(this.catchup).pipe(
            withLatestFrom(authorisationService.promiseToGetHasAccess(LinkAuthService.ViewLink)),
            switchMap(([canEdit, hasLinkAccess]) => hasLinkAccess
                ? this.peerCatchupService.getLinksForCatchup(this.catchup.speedCatchupId).pipe(map(() => canEdit))
                : of(canEdit)),
            this.takeUntilDestroyed(),
        ).subscribe((canEditCatchup) => {
            this.canEditCatchup = canEditCatchup;
            this.initAccordionEntries();
            if (data.enterEditMode) {
                this.isEditingUpdater.next(true);
            }
        });

        kanbanService.hasAnyEditableBoard().pipe(
            this.takeUntilDestroyed(),
        ).subscribe((hasEditableBoard) => this.hasEditableBoard = hasEditableBoard);

        this.scrollFacilitatorIntoView.pipe(
            filter(() => !!this.data.showFacilitatorSection), // not going to emit if not scrolling to view
            debounceTime(1500), // debounce 1.5 sec (dialog animation) as this till be triggerred from itemRendered from accordion -> only want to scroll once
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            this.facilitatorSection?.element.nativeElement.scrollIntoView();
            if (this.isEditing) {
                this.facilitatorEditor?.getElementToFocus();
            }
        });

        this.scrollToRatingTypeSection.pipe(
            filter(() => !!this.data.focusRatingType),
            debounceTime(1500),
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            const ratingTypeComponent = this.ratingTypeComponents.find((i) => i.ratingType === this.data.focusRatingType);
            ratingTypeComponent?.scrollIntoView();
        });
    }

    public onAccordionItemRendered() {
        // this is called when accordion items are rendered - debounceTime from the followings will take care of duplicate triggers
        this.scrollFacilitatorIntoView.next();
        this.scrollToRatingTypeSection.next();
    }

    public get areBothPeopleActive() {
        return !!(this.catchup?.person1?.isActive() && this.catchup?.person2?.isActive());
    }

    public get isCatchupDetached() {
        return this.catchup?.entityAspect.entityState.isDetached() || this.catchup?.entityAspect.entityState.isDeleted();
    }

    public get catchupValid() {
        return this.catchup?.entityAspect.validateEntity() &&
            this.catchup.ratings.every((rating) => rating.entityAspect.validateEntity());
    }

    public get hasChanges() {
        return this.catchup?.entityAspect.entityState.isAddedModifiedOrDeleted() ||
            this.catchup?.ratings.some((rating) => rating.entityAspect.entityState.isAddedModifiedOrDeleted());
    }

    public get canSave() {
        // catchup must be valid and either something has changed or enter from Save and review (hide history and enter edit mode)
        return (this.hasChanges || (this.data.hideHistoryButton && this.data.enterEditMode)) && this.catchupValid;
    }

    public deleteCatchup() {
        this.peerCatchupUiService.deleteCatchup(this.catchup).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.resolve(this.catchup));
    }

    public showCatchupHistory() {
        this.peerCatchupUiService.showCatchupHistoryChart(this.catchup.person1!, this.catchup.person2!).subscribe();
    }

    @Autobind
    public scheduleNextCatchup() {
        return this.peerCatchupUiService.scheduleCatchupFromPreviousCatchup(this.catchup);
    }

    @Autobind
    public saveAndClose() {
        if (this.canSave) {
            return this.peerCatchupService.saveEntities([this.catchup, ...this.catchup.ratings, ...this.catchup.links!]).pipe(
                tap(() => {
                    this.peerCatchupService.emitCatchupChange(this.catchup);
                    this.resolve(this.catchup);
                }),
            );
        } else {
            // shouldn't happen here as the button should be disabled -> won't emit
            // - this will make the return type correct
            return EMPTY;
        }
    }

    public cancel() {
        if (this.isEditing) {
            if (this.hasChanges) {
                this.dialogService.openConfirmationDialog({
                    title: "Cancel & Discard Changes",
                    message: "Are you sure you want to cancel editing this Peer Catch-up and discard the changes you have made?",
                    confirmButtonText: "Yes",
                    cancelButtonText: "No",
                }).pipe(
                    tap(() => this.isEditingUpdater.next(false)),
                    switchMap(() => this.editingUpdated),
                    take(1),
                    this.takeUntilDestroyed(),
                ).subscribe(() => {
                    // do this in the next digest cycle to avoid directly changing entity from button click
                    setTimeout(() => {
                        this.catchup.entityAspect.rejectChanges();
                        this.catchup.ratings.forEach((rating) => rating.entityAspect.rejectChanges());
                    });
                });
            } else {
                this.isEditingUpdater.next(false);
            }
        } else {
            super.cancel();
        }
    }

    public enterEditMode() {
        this.isEditingUpdater.next(true);
        this.data.enterEditMode = true;
        this.data.focusRatingType = undefined;
        this.data.showFacilitatorSection = undefined;
    }

    @Autobind
    public addAction() {
        return this.kanbanUiService.createItem(undefined, { forceAllBoards: true }).pipe(
            switchMap((item) => this.linkService.createLinkForPeerCatchup(this.catchup, item.itemId)),
            switchMap((l) => {
                // only save action link when editing an existing pcu
                return this.catchup.speedCatchupId > 0 ? this.peerCatchupService.saveEntities(l) : of();
            }),
            tap(() => this.initAccordionEntries()),
        );
    }

    public updateLinks(item: Item) {
        if (item.entityAspect.entityState.isDetached() || item.entityAspect.entityState.isDeleted()) {
            this.initAccordionEntries();
        }
    }

    private initAccordionEntries() {
        this.accordionEntries = this.catchupRatingTypes.sort((a, b) => a.ordinal - b.ordinal)
            .map((ratingType) => ({
                ratingType,
                template: "ratingTypeTemplate",
                titleTemplate: "ratingTypeTitleTemplate",
            }));
        if (this.catchup.facilitatorPersonId) {
            this.accordionEntries.push({
                template: "facilitatorTemplate",
                titleTemplate: "facilitatorTitleTemplate",
            });
        }
        if (this.catchup.links.length) {
            this.catchup.links.sort(ItemUtilities.getItemLinkStatusAndItemCodeSortComparator());
            this.accordionEntries.push({
                template: "linksTemplate",
                titleTemplate: "linksTitleTemplate",
            });
        }

        // next digest cycle to workaround error from dx
        setTimeout(() => this.selectedEntries = this.accordionEntries);
    }
}
